import { Form, Formik, FormikProps, FormikValues } from "formik";
import { delay } from "lodash";
import React, { useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import ImageSlideshow from "../../components/shared-components/Image/ImageSlideshow";
import CustomModal from "../../components/shared-components/Modal/CustomModal";
import EmailVerificationModal from "../../components/shared-components/Modal/login/EmailVerificationModal";
import TopRoundedSpinner from "../../components/shared-components/Spinner/TopRoundedSpinner";
import BrandButton from "../../components/shared-components/custom-components/BrandButton";
import BrandHeading from "../../components/shared-components/custom-components/heading/BrandHeading";
import FormikInput from "../../components/shared-components/input/Input/FormikInput";
import { BrandContext } from "../../contexts/BrandContext";
import { ButtonClickPayloadGA } from "../../models/types/GAPayload.types";
import { authLoginHalfAction } from "../../redux/actions/auth.actions";
import { getInviteAction } from "../../redux/actions/invites.action";
import { AppState } from "../../redux/reducers";
import { AuthState } from "../../redux/reducers/auth.reducer";
import { BrandState } from "../../redux/reducers/brand.reducer";
import { InviteState } from "../../redux/reducers/invite.reducer";
import {
  authSelector,
  brandConfigSelector,
  brandSelector,
} from "../../redux/selectors/app.selectors";
import {
  EMAIL_CODE,
  INVITE_ID,
  RECAPTCHA_TOKEN,
  REFERRAL_CODE,
  webStorageService,
} from "../../services/WebStorageService";
import Utils from "../../utils/Utils";
import { BrandConfigKeys, GAEventName, Providers } from "../../utils/enums";
import { UserState } from "../../redux/reducers/user.reducer";

interface LoginPageProps { }

const LoginPageView: React.FC<LoginPageProps> = () => {
  const brandContext = useContext(BrandContext);
  const dispatch = useDispatch();
  const [showEmailConfirmationModal, setShowEmailConfirmationModal] =
    useState(false);

  const {
    brand,
    configEntities: brandConfig,
    brand_socials_credentials,
  }: BrandState = useSelector(brandSelector);
  const {
    is_login_page_ui_disabled,
    is_login_checking_modal_enabled,
  }: AuthState = useSelector(authSelector);

  const inviteState: InviteState = useSelector(
    (state: AppState) => state.invite
  );
  const { userData }: UserState = useSelector((state: AppState) => state.user);

  const reCaptchaRef = React.useRef<ReCAPTCHA>(null);

  const onReCAPTCHAChange = (captchaCode: any) => {
    // If the reCAPTCHA code is null or undefined indicating that
    // the reCAPTCHA was expired then return early
    if (!captchaCode) {
      return;
    }
    // Reset the reCAPTCHA so that it can be executed again if user
    // submits another email.
    reCaptchaRef?.current?.reset?.();
  };

  const loginConfigs = {
    emailLoginConfig: brandConfigSelector(
      brandConfig,
      BrandConfigKeys.EMAIL_LOGIN_CONFIG
    )?.value,
    recaptcha: brandConfigSelector(
      brandConfig,
      BrandConfigKeys.RECAPTCHA_CONFIG
    )?.value,
  };

  const isRecaptchaEnable = loginConfigs.recaptcha?.is_enabled;

  const handleSubmit = async () => {
    //   // Execute the reCAPTCHA when the form is submitted
    if (isRecaptchaEnable) {
      const token = await reCaptchaRef?.current?.executeAsync?.();
      return token;
    }
    return null;
  };
  useEffect(() => {
    // To open the Email verification modal when email_code is still present
    // Not to show on auth/login/verify page
    if (!!webStorageService.getValue(EMAIL_CODE)) {
      setShowEmailConfirmationModal(true);
    }
    const inviteId = webStorageService.getValue(INVITE_ID);
    if (!!inviteId) {
      dispatch(getInviteAction({ invite_id: inviteId }));
    }
  }, [dispatch]);

  const brandReCaptchaCredentials =
    brand_socials_credentials[Providers.RECAPTCHA];

  return (
    <>
      <CustomModal
        theme="primary"
        open={!!is_login_checking_modal_enabled}
        onClose={() => { }}
      >
        <div className="flex flex-col items-center justify-center px-6 mb-2 space-y-7 min-h-44 w-fit">
          <h1 className="text-lg font-bold xs:text-xl">
            Connecting to your account
          </h1>

          <TopRoundedSpinner size="md" />
        </div>
      </CustomModal>

      <div className="flex flex-col items-center justify-center w-full max-w-4xl min-h-screen p-8 mx-auto text-center backdrop-blur-sm" style={{ minHeight: '100dvh' }}>
        {showEmailConfirmationModal && (
          <EmailVerificationModal
            isEmailLoginOTPType={loginConfigs.emailLoginConfig?.is_type_otp}
            showEmailVerificationModal={showEmailConfirmationModal}
            setShowEmailVerificationModal={setShowEmailConfirmationModal}
          />
        )}
        <div className="flex justify-center">
          <img 
            src="/images/humana-nsga-logo.svg" 
            alt="Humana NSGA Logo" 
            className="w-full max-w-[300px] sm:max-w-[400px] h-auto"
          />
        </div>

        <BrandHeading className="mb-8 mt-14 !text-2xl md:!text-5xl !pb-0 relative text-gray-700">
          Welcome to the Humana Cognitive Games<span className="md:text-lg text-sm font-bold relative -top-2 md:-top-5 p-1">TM</span>
        </BrandHeading>
        <h2 className="mb-3 text-base font-light md:mb-8 md:text-2xl text-gray-700 flex flex-col justify-center items-center">
          <p className="md:max-w-[70%] lg:max-w-[70%] max-w-full text-center">Challenge your mind and elevate your game with a mix of fun, brain-boosting activities and easy lifestyle tips designed for active seniors.</p>
          <p className="mt-4 md:mt-8">Ready to play!</p>
        </h2>

        <div className="flex flex-col justify-center w-full lg:items-center lg:flex-row lg:max-w-2xl">
          {
            // Only show email when email_config is enabled or Both
            loginConfigs.emailLoginConfig?.is_enabled &&
              !inviteState.loading ? (
              <div id="email_login" className="flex-1">
                <Formik
                  validateOnMount
                  initialValues={{
                    email: inviteState.invite?.address ?? "",
                  }}
                  validationSchema={Yup.object().shape({
                    email: Yup.string()
                      .email("Please enter a valid email")
                      .required("Email is required"),
                  })}
                  onSubmit={async (value, { resetForm }) => {
                    const reCaptchaToken = await handleSubmit();

                    const invite_id = webStorageService.getValue(INVITE_ID);
                    const referral_code =
                      webStorageService.getValue(REFERRAL_CODE);
                    dispatch(
                      authLoginHalfAction({
                        email: value.email,
                        reCaptchaToken:
                          loginConfigs.recaptcha?.is_enabled && reCaptchaToken
                            ? reCaptchaToken
                            : undefined,
                        invite_id,
                        referral_code,
                      })
                    );
                    delay(() => {
                      setShowEmailConfirmationModal(true);
                    }, 500);
                    Utils.GAEvent<ButtonClickPayloadGA>(
                      GAEventName.BUTTON_CLICK,
                      {
                        event_category: "Button",
                        event_label: "Log In: Email",
                      }
                    );
                  }}
                >
                  {(formikProps: FormikProps<FormikValues>) => {
                    return (
                      <Form>
                        <div className="w-full">
                          <div className="w-full max-w-sm mx-auto mb-4 text-left">
                            <FormikInput
                              disabled={!!inviteState.invite?.address}
                              placeholder={"Please enter your email"}
                              name={"email"}
                              label={`Email`}
                              autoComplete="off"
                            />
                          </div>
                          <div className="flex items-center justify-center pt-3">
                            <BrandButton
                              disabled={
                                !formikProps.isValid ||
                                !!is_login_page_ui_disabled
                              }
                              //@ts-ignore
                              onClick={formikProps.handleSubmit}
                              type="submit"
                              theme="primary"
                              className="h-12 text-base"
                            >
                              Log In/Sign Up
                            </BrandButton>
                          </div>
                        </div>
                      </Form>
                    );
                  }}
                </Formik>
              </div>
            ) : null
          }
        </div>

        <div className="mt-10 text-center">
          <div className="text-lg font-semibold md:text-3xl">
            Get{" "}
            {`${brandContext.sign_up_credit_points} ${brandContext.currency ?? "Points"
              }`}
          </div>
          <div className="text-sm">When you sign up!</div>
          <div className="mt-6 text-sm opacity-50">
            {brand?.privacy_policy_url && (
              <a
                className="inline-block mx-2 transition duration-300 md:mx-4 hover:scale-105"
                href={brand.privacy_policy_url}
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
            )}
            {brandContext?.setOpenCookiePolicy && (
              <button
                className="inline-block mx-2 transition duration-300 md:mx-4 whitespace-nowrap hover:scale-105"
                onClick={() => {
                  brandContext?.setOpenCookiePolicy?.(true);
                }}
              >
                Cookie Policy
              </button>
            )}

            {brand?.terms_and_condition_url && (
              <a
                className="inline-block mx-2 transition duration-300 md:mx-4 whitespace-nowrap hover:scale-105"
                href={brand.terms_and_condition_url}
                target="_blank"
                rel="noreferrer"
              >
                Terms & Conditions
              </a>
            )}
          </div>

          {loginConfigs.recaptcha?.is_enabled &&
            brandReCaptchaCredentials?.site_key && (
              <div className="mt-6">
                <small className="text-sm opacity-30">
                  This site is protected by reCAPTCHA.
                  <ReCAPTCHA
                    badge="inline"
                    ref={reCaptchaRef}
                    size="invisible"
                    sitekey={brandReCaptchaCredentials?.site_key}
                    onChange={onReCAPTCHAChange}
                  />
                  {/* <a
            href="https://policies.google.com/privacy"
            className="hover:underline"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://policies.google.com/terms"
            className="hover:underline"
          >
            Terms of Service
          </a>{" "}
          apply. */}
                </small>
              </div>
            )}
        </div>
      </div>
    </>
  );
};

export default React.memo(LoginPageView);
